import { createBrowserRouter, RouterProvider } from "react-router";

import { ROUTES } from "../router/router-url";
import { ContinueRedirect } from "../components/ContinueRedirect.tsx";
import ContinueTo from "../components/ContinueTo.tsx";

import { useAuth } from "../store/useAuth.ts";

import { AuthStatusEnum, UserRoleEnum } from "../types/auth.ts";
import { Spin } from "antd";
import * as Sentry from "@sentry/react";
import { lazyImport } from "../utils/lazyImport";

const PUBLIC_ROUTER = createBrowserRouter([
  {
    index: true,
    path: ROUTES.LOGIN.PATH,
    lazy: lazyImport(() => import("../pages/login/Index.tsx")),
  },
  {
    path: "*",
    element: <ContinueRedirect to={ROUTES.LOGIN.PATH} />,
  },
]);

const SUPERADMIN_ROUTER = createBrowserRouter([
  {
    lazy: lazyImport(() => import("../layouts/Layout")),
    path: "/",
    children: [
      { path: "/tickets", lazy: lazyImport(() => import("../pages/tickets/Index.tsx")) },
      { path: "/tickets/:id", lazy: lazyImport(() => import("../pages/tickets/Detail.tsx")) },
      {
        path: ROUTES.ERP_CONSENT.PATH,
        lazy: lazyImport(() => import("../pages/erp-consent/Index.tsx")),
      },
      {
        index: true,
        path: ROUTES.INDEX.PATH,
        lazy: lazyImport(() => import("../pages/dashboard/Index.tsx")),
      },
      {
        path: ROUTES.EMPLOYEES.PATH,
        lazy: lazyImport(() => import("../pages/employees/Index.tsx")),
        children: [
          {
            path: ROUTES.EMPLOYEES.CREATE.PATH,
            lazy: lazyImport(() => import("../pages/employees/Create.tsx")),
          },
        ],
      },
      { path: "/payrolls", lazy: lazyImport(() => import("../pages/payrolls/Index.tsx")) },
      { path: "/payrolls/:year/:month", lazy: lazyImport(() => import("../pages/payrolls/Detail.tsx")) },
      {
        path: ROUTES.SALARY_ACCOUNT.PATH,
        lazy: lazyImport(() => import("../pages/salary-account/Index.tsx")),
      },
      { path: ROUTES.COMPANY_DEVICES.PATH, lazy: lazyImport(() => import("../pages/company-devices/Index.tsx")) },
      {
        path: ROUTES.WORKSPACE.PATH,
        lazy: lazyImport(() => import("../pages/workspace/Index.tsx")),
      },
      {
        path: ROUTES.MIKRO_PERSONNEL_SYNC.PATH,
        lazy: lazyImport(
          () => import("../pages/mikro-personnel-sync/Index.tsx"),
        ),
      },
      { path: ROUTES.MEAL_PLANS.PATH, lazy: lazyImport(() => import("../pages/meal-plans/Index.tsx")) },
      { path: ROUTES.APP_USAGE.PATH, lazy: lazyImport(() => import("../pages/app-usage/Index.tsx")) },
      { path: ROUTES.SHIFT_PLANS.PATH, lazy: lazyImport(() => import("../pages/shift-plans/Index.tsx")) },
      {
        path: ROUTES.ANNOUNCEMENT.PATH,
        lazy: lazyImport(() => import("../pages/announcement/Index.tsx")),
        children: [
          {
            path: ROUTES.ANNOUNCEMENT.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/announcement/Edit.tsx")),
          },
          {
            path: ROUTES.ANNOUNCEMENT.SURVEY.PATH,
            lazy: lazyImport(
              () => import("../pages/announcement/SurveyResults.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.WORK_CALENDAR.PATH,
        lazy: lazyImport(() => import("../pages/work-calendar/Index.tsx")),
        children: [
          {
            path: ROUTES.WORK_CALENDAR.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/work-calendar/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.EMPLOYEES.EDIT.PATH,
        lazy: lazyImport(() => import("../pages/employees/Edit.tsx")),
      },
      {
        path: ROUTES.LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/leaves/Index.tsx")),
      },
      {
        path: ROUTES.OVERTIME.PATH,
        lazy: lazyImport(() => import("../pages/overtime/Index.tsx")),
      },
      {
        path: ROUTES.ACTIVITY.PATH,
        lazy: lazyImport(() => import("../pages/activity/Index.tsx")),
      },
      {
        path: ROUTES.LEAVE_RIGHT_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/leave-right-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.GUEST_ENTRANCE_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/guest-entrance-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.EMPLOYMENT_START_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/employment-start-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.USER_LEAVE_REPORTS.PATH,
        lazy: lazyImport(() => import("../pages/user-leave-reports/Index.tsx")),
      },
      // TODO: Vardiya Raporu sayfası geçici olarak devre dışı.
      // {
      //   path: ROUTES.EMPLOYEE_SHIFT_REPORTS.PATH,
      //   lazy: lazyImport(
      //     () => import("../pages/employee-shift-report/Index.tsx"),
      //   ),
      // },
      {
        path: ROUTES.PUANTAJ.PATH,
        lazy: lazyImport(() => import("../pages/puantaj/Index.tsx")),
      },
      {
        path: ROUTES.DAILY_SHIFTS.PATH,
        lazy: lazyImport(() => import("../pages/daily-shifts/Index.tsx")),
      },
      {
        path: ROUTES.MY_OVERTIMES.PATH,
        lazy: lazyImport(() => import("../pages/my-overtimes/Index.tsx")),
      },
      {
        path: ROUTES.ASSIGNMENTS.PATH,
        lazy: lazyImport(() => import("../pages/assignments/Index.tsx")),
      },
      {
        path: ROUTES.GUESTS.PATH,
        lazy: lazyImport(() => import("../pages/guests/Index.tsx")),
        children: [
          {
            path: ROUTES.GUESTS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/guests/Edit.tsx")),
          },
          {
            path: ROUTES.GUESTS.DETAIL.PATH,
            lazy: lazyImport(() => import("../pages/guests/Detail.tsx")),
          },
        ],
      },
      {
        path: ROUTES.GATES.PATH,
        lazy: lazyImport(() => import("../pages/gates/Index.tsx")),
        children: [
          {
            path: ROUTES.GATES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/gates/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.MY_LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/my-leaves/Index.tsx")),
      },
      {
        path: ROUTES.LEAVE_TYPES.PATH,
        lazy: lazyImport(() => import("../pages/leave-types/Index.tsx")),
        children: [
          {
            path: ROUTES.LEAVE_TYPES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/leave-types/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.PERSONNEL_FILE_FLOWS.PATH,
        lazy: lazyImport(() => import("../pages/personnel-file-flows/Index.tsx")),
      },
      {
        path: ROUTES.PERSONNEL_FILE_REQUIREMENT_DEFINITIONS.PATH,
        lazy: lazyImport(
          () =>
            import("../pages/personnel-file-requirement-definitions/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.PERSONNEL_FILE_REQUIREMENT_DEFINITIONS.EDIT.PATH,
            lazy: lazyImport(
              () =>
                import(
                  "../pages/personnel-file-requirement-definitions/Edit.tsx"
                ),
            ),
          },
        ],
      },
      {
        path: ROUTES.BULK_LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/bulk-leaves/Index.tsx")),
      },
      {
        path: ROUTES.LEAVE_APPROVAL_PROCESS.PATH,
        lazy: lazyImport(
          () => import("../pages/leave-approval-process/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.LEAVE_APPROVAL_PROCESS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/leave-approval-process/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.OVERTIME_APPROVAL_PROCESS.PATH,
        lazy: lazyImport(
          () => import("../pages/overtime-approval-process/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.OVERTIME_APPROVAL_PROCESS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/overtime-approval-process/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.CALENDAR_HOLIDAYS.PATH,
        lazy: lazyImport(() => import("../pages/calendar-holidays/Index.tsx")),
        children: [
          {
            path: ROUTES.CALENDAR_HOLIDAYS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/calendar-holidays/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.REALMS.PATH,
        lazy: lazyImport(() => import("../pages/realms/Index.tsx")),
        children: [
          {
            path: ROUTES.REALMS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/realms/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.FEATURE_FLAGS.PATH,
        lazy: lazyImport(() => import("../pages/feature-flags/Index.tsx")),
        children: [
          {
            path: ROUTES.FEATURE_FLAGS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/feature-flags/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.REALMS.DETAIL.PATH,
        lazy: lazyImport(() => import("../pages/realms/Detail.tsx")),
        children: [
          {
            path: "edit/:id",
            lazy: lazyImport(() => import("../pages/realms/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.FACTORIES.PATH,
        lazy: lazyImport(() => import("../pages/factories/Index.tsx")),
        children: [
          {
            path: ROUTES.FACTORIES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/factories/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.UNITS.PATH,
        lazy: lazyImport(() => import("../pages/units/Index.tsx")),
        children: [
          {
            path: ROUTES.UNITS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/units/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.UNITS.ORG_CHART.PATH,
        lazy: lazyImport(() => import("../pages/units/UnitOrgChart.tsx")),
      },
      {
        path: ROUTES.APP_VERSION.PATH,
        lazy: lazyImport(() => import("../pages/app-version/Main.tsx")),
        children: [
          {
            index: true,
            lazy: lazyImport(
              () => import("../pages/app-version/Management.tsx"),
            ),
          },
          {
            path: ROUTES.APP_VERSION.CREATE.PATH,
            lazy: lazyImport(() => import("../pages/app-version/Edit.tsx")),
          },
          {
            path: ROUTES.APP_VERSION.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/app-version/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.VERSION_CHECK.PATH,
        lazy: lazyImport(() => import("../pages/version-check/Index.tsx")),
      },
      {
        index: true,
        path: ROUTES.LOGIN.PATH,
        element: <ContinueTo fail={ROUTES.INDEX.PATH} />,
      },
    ],
  },
]);

const ADMIN_ROUTER = createBrowserRouter([
  {
    lazy: lazyImport(() => import("../layouts/Layout")),
    path: "/",
    children: [
      { path: "/tickets", lazy: lazyImport(() => import("../pages/tickets/Index.tsx")) },
      { path: "/tickets/:id", lazy: lazyImport(() => import("../pages/tickets/Detail.tsx")) },
      {
        path: ROUTES.ERP_CONSENT.PATH,
        lazy: lazyImport(() => import("../pages/erp-consent/Index.tsx")),
      },
      {
        index: true,
        path: ROUTES.INDEX.PATH,
        lazy: lazyImport(() => import("../pages/dashboard/Index.tsx")),
      },
      {
        path: ROUTES.EMPLOYEES.PATH,
        lazy: lazyImport(() => import("../pages/employees/Index.tsx")),
        children: [
          {
            path: ROUTES.EMPLOYEES.CREATE.PATH,
            lazy: lazyImport(() => import("../pages/employees/Create.tsx")),
          },
        ],
      },
      { path: "/payrolls", lazy: lazyImport(() => import("../pages/payrolls/Index.tsx")) },
      { path: "/payrolls/:year/:month", lazy: lazyImport(() => import("../pages/payrolls/Detail.tsx")) },
      {
        path: ROUTES.SALARY_ACCOUNT.PATH,
        lazy: lazyImport(() => import("../pages/salary-account/Index.tsx")),
      },
      { path: ROUTES.COMPANY_DEVICES.PATH, lazy: lazyImport(() => import("../pages/company-devices/Index.tsx")) },
      {
        path: ROUTES.WORKSPACE.PATH,
        lazy: lazyImport(() => import("../pages/workspace/Index.tsx")),
      },
      {
        path: ROUTES.MIKRO_PERSONNEL_SYNC.PATH,
        lazy: lazyImport(
          () => import("../pages/mikro-personnel-sync/Index.tsx"),
        ),
      },
      { path: ROUTES.MEAL_PLANS.PATH, lazy: lazyImport(() => import("../pages/meal-plans/Index.tsx")) },
      { path: ROUTES.APP_USAGE.PATH, lazy: lazyImport(() => import("../pages/app-usage/Index.tsx")) },
      { path: ROUTES.SHIFT_PLANS.PATH, lazy: lazyImport(() => import("../pages/shift-plans/Index.tsx")) },
      {
        path: ROUTES.ANNOUNCEMENT.PATH,
        lazy: lazyImport(() => import("../pages/announcement/Index.tsx")),
        children: [
          {
            path: ROUTES.ANNOUNCEMENT.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/announcement/Edit.tsx")),
          },
          {
            path: ROUTES.ANNOUNCEMENT.SURVEY.PATH,
            lazy: lazyImport(
              () => import("../pages/announcement/SurveyResults.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.EMPLOYEES.EDIT.PATH,
        lazy: lazyImport(() => import("../pages/employees/Edit.tsx")),
      },
      {
        path: ROUTES.LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/leaves/Index.tsx")),
      },
      {
        path: ROUTES.LEAVE_RIGHT_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/leave-right-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.GUEST_ENTRANCE_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/guest-entrance-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.EMPLOYMENT_START_REPORTS.PATH,
        lazy: lazyImport(
          () => import("../pages/employment-start-reports/Index.tsx"),
        ),
      },
      {
        path: ROUTES.USER_LEAVE_REPORTS.PATH,
        lazy: lazyImport(() => import("../pages/user-leave-reports/Index.tsx")),
      },
      // TODO: Vardiya Raporu sayfası geçici olarak devre dışı.
      // {
      //   path: ROUTES.EMPLOYEE_SHIFT_REPORTS.PATH,
      //   lazy: lazyImport(
      //     () => import("../pages/employee-shift-report/Index.tsx"),
      //   ),
      // },
      {
        path: ROUTES.PUANTAJ.PATH,
        lazy: lazyImport(() => import("../pages/puantaj/Index.tsx")),
      },
      {
        path: ROUTES.DAILY_SHIFTS.PATH,
        lazy: lazyImport(() => import("../pages/daily-shifts/Index.tsx")),
      },
      {
        path: ROUTES.OVERTIME.PATH,
        lazy: lazyImport(() => import("../pages/overtime/Index.tsx")),
      },
      {
        path: ROUTES.MY_OVERTIMES.PATH,
        lazy: lazyImport(() => import("../pages/my-overtimes/Index.tsx")),
      },
      {
        path: ROUTES.ASSIGNMENTS.PATH,
        lazy: lazyImport(() => import("../pages/assignments/Index.tsx")),
      },
      {
        path: ROUTES.REALMS.DETAIL.PATH,
        lazy: lazyImport(() => import("../pages/realms/Detail.tsx")),
        children: [
          {
            path: "edit/:id",
            lazy: lazyImport(() => import("../pages/realms/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.WORK_CALENDAR.PATH,
        lazy: lazyImport(() => import("../pages/work-calendar/Index.tsx")),
        children: [
          {
            path: ROUTES.WORK_CALENDAR.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/work-calendar/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.WORK_CALENDAR.PATH,
        lazy: lazyImport(() => import("../pages/work-calendar/Index.tsx")),
        children: [
          {
            path: ROUTES.WORK_CALENDAR.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/work-calendar/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.GUESTS.PATH,
        lazy: lazyImport(() => import("../pages/guests/Index.tsx")),
        children: [
          {
            path: ROUTES.GUESTS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/guests/Edit.tsx")),
          },
          {
            path: ROUTES.GUESTS.DETAIL.PATH,
            lazy: lazyImport(() => import("../pages/guests/Detail.tsx")),
          },
        ],
      },
      {
        path: ROUTES.GATES.PATH,
        lazy: lazyImport(() => import("../pages/gates/Index.tsx")),
        children: [
          {
            path: ROUTES.GATES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/gates/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.LEAVE_TYPES.PATH,
        lazy: lazyImport(() => import("../pages/leave-types/Index.tsx")),
        children: [
          {
            path: ROUTES.LEAVE_TYPES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/leave-types/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.PERSONNEL_FILE_FLOWS.PATH,
        lazy: lazyImport(() => import("../pages/personnel-file-flows/Index.tsx")),
      },
      {
        path: ROUTES.PERSONNEL_FILE_REQUIREMENT_DEFINITIONS.PATH,
        lazy: lazyImport(
          () =>
            import("../pages/personnel-file-requirement-definitions/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.PERSONNEL_FILE_REQUIREMENT_DEFINITIONS.EDIT.PATH,
            lazy: lazyImport(
              () =>
                import(
                  "../pages/personnel-file-requirement-definitions/Edit.tsx"
                ),
            ),
          },
        ],
      },
      {
        path: ROUTES.MY_LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/my-leaves/Index.tsx")),
      },
      {
        path: ROUTES.BULK_LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/bulk-leaves/Index.tsx")),
      },
      {
        path: ROUTES.LEAVE_APPROVAL_PROCESS.PATH,
        lazy: lazyImport(
          () => import("../pages/leave-approval-process/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.LEAVE_APPROVAL_PROCESS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/leave-approval-process/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.OVERTIME_APPROVAL_PROCESS.PATH,
        lazy: lazyImport(
          () => import("../pages/overtime-approval-process/Index.tsx"),
        ),
        children: [
          {
            path: ROUTES.OVERTIME_APPROVAL_PROCESS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/overtime-approval-process/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.CALENDAR_HOLIDAYS.PATH,
        lazy: lazyImport(() => import("../pages/calendar-holidays/Index.tsx")),
        children: [
          {
            path: ROUTES.CALENDAR_HOLIDAYS.EDIT.PATH,
            lazy: lazyImport(
              () => import("../pages/calendar-holidays/Edit.tsx"),
            ),
          },
        ],
      },
      {
        path: ROUTES.FACTORIES.PATH,
        lazy: lazyImport(() => import("../pages/factories/Index.tsx")),
        children: [
          {
            path: ROUTES.FACTORIES.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/factories/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.UNITS.PATH,
        lazy: lazyImport(() => import("../pages/units/Index.tsx")),
        children: [
          {
            path: ROUTES.UNITS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/units/Edit.tsx")),
          },
        ],
      },
      {
        path: ROUTES.UNITS.ORG_CHART.PATH,
        lazy: lazyImport(() => import("../pages/units/UnitOrgChart.tsx")),
      },
      {
        index: true,
        path: ROUTES.LOGIN.PATH,
        element: <ContinueTo fail={ROUTES.INDEX.PATH} />,
      },
    ],
  },
]);

const PRIVATE_ROUTER = createBrowserRouter([
  {
    lazy: lazyImport(() => import("../layouts/Layout")),
    path: "/",
    children: [
      { path: "/tickets", lazy: lazyImport(() => import("../pages/tickets/Index.tsx")) },
      { path: "/tickets/:id", lazy: lazyImport(() => import("../pages/tickets/Detail.tsx")) },
      {
        path: ROUTES.ERP_CONSENT.PATH,
        lazy: lazyImport(() => import("../pages/erp-consent/Index.tsx")),
      },
      {
        index: true,
        path: ROUTES.INDEX.PATH,
        lazy: lazyImport(() => import("../pages/dashboard/Index.tsx")),
      },
      {
        path: ROUTES.MY_LEAVES.PATH,
        lazy: lazyImport(() => import("../pages/my-leaves/Index.tsx")),
      },
      {
        path: ROUTES.MY_OVERTIMES.PATH,
        lazy: lazyImport(() => import("../pages/my-overtimes/Index.tsx")),
      },
      {
        path: ROUTES.ASSIGNMENTS.PATH,
        lazy: lazyImport(() => import("../pages/assignments/Index.tsx")),
      },
      {
        path: "*",
        element: <ContinueTo fail={ROUTES.INDEX.PATH} />,
      },
      {
        path: ROUTES.ANNOUNCEMENT.PATH,
        lazy: lazyImport(() => import("../pages/announcement/Index.tsx")),
      },
      {
        path: ROUTES.GUESTS.PATH,
        lazy: lazyImport(() => import("../pages/guests/Index.tsx")),
        children: [
          {
            path: ROUTES.GUESTS.EDIT.PATH,
            lazy: lazyImport(() => import("../pages/guests/Edit.tsx")),
          },
          {
            path: ROUTES.GUESTS.DETAIL.PATH,
            lazy: lazyImport(() => import("../pages/guests/Detail.tsx")),
          },
        ],
      },
    ],
  },
]);

export default function Router() {
  const auth = useAuth();

  if (auth.status === AuthStatusEnum.LOADING) {
    return <Spin spinning={true} fullscreen />;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Bir Hata Oluştu
              </h2>
              <p className="text-gray-600 mb-6">
                Beklenmeyen bir hata oluştu. Lütfen sayfayı yenileyin veya
                tekrar deneyin.
              </p>
              <div className="space-y-3">
                <button
                  onClick={resetError}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Tekrar Dene
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Sayfayı Yenile
                </button>
              </div>
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">
                  Teknik Detaylar
                </summary>
                <pre className="mt-2 text-xs text-gray-400 bg-gray-100 p-2 rounded overflow-auto">
                  {error ? String(error) : "Bilinmeyen hata"}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}
      showDialog={false}
    >
      <RouterProvider
        router={
          auth.status !== AuthStatusEnum.LOGGED_IN
            ? PUBLIC_ROUTER
            : auth.user?.user.role === UserRoleEnum.SUPERADMIN
              ? SUPERADMIN_ROUTER
              : auth.user?.user.role === UserRoleEnum.ADMIN
                ? ADMIN_ROUTER
                : PRIVATE_ROUTER
        }
      />
    </Sentry.ErrorBoundary>
  );
}
